import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { view, dialogs, i18next, customHtml } from '@yola/ws-sdk';
import {
  Button,
  ButtonGroup,
  Modal,
  Panel,
  PanelGroup,
  Text,
  designSystem,
  AceEditor,
  FieldStatus,
} from '@yola/ws-ui';
import { ViewportConsumer } from 'yola-editor/src/js/utils/viewport-provider';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import CustomHtmlRenderOption from 'src/js/modules/common/components/custom-html-render-option';
import constants from '../constants/common';
import * as keyValues from '../constants/key-values';
import getElementContent from '../helpers/get-element-content';
import updateContent from '../helpers/update-content';
import preventEditorZooming from '../helpers/ios-workarounds/prevent-editor-zooming';

const { renderStatuses, RUNTIME_RENDERING_ATTRIBUTE } = customHtml.constants;
const { SimpleSelect, Heading, Box, HorizontalStack } = designSystem;
const MENU_WIDTH = '160px';
const DESKTOP_BREAKPOINT = '992px';

const EmbedHtmlSettingsDialog = ({ elementId, onDialogMainAction, onDialogCancel }) => {
  const element = view.accessors.getLiveElement(elementId);
  const position = dialogs.helpers.getPositionByElement(
    element,
    constants.WIDGET_WIDTH,
    constants.WIDGET_HEIGHT
  );
  const content = getElementContent(element);
  const isRuntimeRenderStatus = customHtml.verifiers.isRuntimeRender();
  const initialIsRuntimeRendering = element.hasAttribute(RUNTIME_RENDERING_ATTRIBUTE);

  const [html, setHtml] = useState(content);
  const [isRuntimeRendering, setIsRuntimeRendering] = useState(initialIsRuntimeRendering);
  const [modalOffsetX, setModalOffsetX] = useState(position.offsetX);
  const [modalOffsetY, setModalOffsetY] = useState(position.offsetY);
  const [error, setError] = useState(null);

  const editorRef = useRef(null);
  const isDesktopView = useMatchBreakpoint(DESKTOP_BREAKPOINT);

  const closeDialog = useCallback(() => {
    dialogs.operations.hide();
  }, []);

  const onModalResize = useCallback(() => {
    if (editorRef.current?.editor) {
      editorRef.current.editor.resize();
    }
  }, []);

  const onHTMLChange = useCallback((newHtml) => {
    setHtml(newHtml);

    if (!newHtml && editorRef.current?.editor) {
      editorRef.current.editor.session.clearAnnotations();
    }
  }, []);

  const hideError = useCallback(() => {
    setError(null);
  }, []);

  const showError = useCallback((annotation) => {
    setError({
      line: annotation.row + 1,
      column: annotation.column + 1,
      message: annotation.text,
    });
  }, []);

  const onHandleSelectModeChange = useCallback((option) => {
    setIsRuntimeRendering(option.value === renderStatuses.RUNTIME);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!editorRef.current?.editor) return;

    const errorAnnotation = editorRef.current.editor
      .getSession()
      .getAnnotations()
      .find((annotation) => annotation.type === 'error');

    if (errorAnnotation) {
      showError(errorAnnotation);
      return;
    }

    onDialogMainAction({
      renderInEditor: isRuntimeRenderStatus && isRuntimeRendering,
    });

    updateContent(element, html, isRuntimeRendering);
    closeDialog();
  }, [
    html,
    isRuntimeRendering,
    isRuntimeRenderStatus,
    element,
    onDialogMainAction,
    showError,
    closeDialog,
  ]);

  const handleCancel = useCallback(() => {
    onDialogCancel({
      renderInEditor: isRuntimeRenderStatus && isRuntimeRendering,
    });
    closeDialog();
  }, [isRuntimeRendering, isRuntimeRenderStatus, onDialogCancel, closeDialog]);

  const onKeyDown = useCallback(
    (event) => {
      const editor = editorRef.current?.editor;
      if (!editor) return;

      const isEditorFocused = editor.isFocused();

      switch (event.key) {
        case keyValues.ESC: {
          onDialogCancel();

          if (isEditorFocused) {
            editor.blur();
          } else {
            closeDialog();
          }
          break;
        }

        case keyValues.ENTER: {
          if (!isEditorFocused) {
            handleSubmit();
          }
          break;
        }

        default:
          break;
      }
    },
    [onDialogCancel, handleSubmit, closeDialog]
  );

  const onDragEnd = useCallback((event, { x, y }) => {
    setModalOffsetX(x);
    setModalOffsetY(y);
  }, []);

  const isMobile = bowser.mobile;
  const options = [
    { value: renderStatuses.WEB_SITE_ONLY, label: i18next.t('Website only') },
    { value: renderStatuses.RUNTIME, label: i18next.t('Website & Editor') },
  ];

  useEffect(() => {
    if (editorRef.current?.editor) {
      preventEditorZooming(editorRef.current.editor);
    }
  }, []);

  return (
    <Modal
      {...position}
      className="ws-html-widget-modal"
      width={constants.WIDGET_WIDTH}
      height={constants.WIDGET_HEIGHT}
      minWidth={constants.WIDGET_MIN_WIDTH}
      minHeight={constants.WIDGET_MIN_HEIGHT}
      resizable={!isMobile}
      draggable={!isMobile}
      fullscreen={isMobile}
      dragHandleSelector=".ws-drag-trigger"
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      onResize={onModalResize}
      onKeyDown={onKeyDown}
      onDragEnd={onDragEnd}
      onFocus={hideError}
    >
      <PanelGroup height="100%">
        <Panel
          className="ws-drag-trigger ws-modal__content"
          height={48}
          align="middle"
          theme="gray-100"
        >
          <Text type="heading-6">{i18next.t('Custom HTML')}</Text>
        </Panel>
        <div className="ws-panel ws-panel--theme-white ws-modal__content ws-html-widget-modal__select-mode">
          <Box>
            <Heading type="heading-6">{i18next.t('HTML code')}</Heading>
          </Box>
          {isDesktopView && (
            <HorizontalStack verticalAlign="center">
              <Heading type="heading-6">{i18next.t('Rendering:')}</Heading>
              <SimpleSelect
                options={options}
                defaultValue={isRuntimeRendering ? options[1] : options[0]}
                transparent
                preventOverflow
                noVerticalSpacing
                noHorizontalSpacing
                menuWidth={MENU_WIDTH}
                menuAlignment="right"
                components={{ Option: CustomHtmlRenderOption }}
                onChange={onHandleSelectModeChange}
              />
            </HorizontalStack>
          )}
        </div>
        <div className="ws-panel ws-panel--theme-white ws-modal__content ws-html-widget-modal__editor">
          <AceEditor
            ref={editorRef}
            className="ws-html-widget-editor"
            mode="html"
            theme="chrome"
            options={{
              tabSize: 2,
            }}
            value={html}
            onChange={onHTMLChange}
            onFocus={hideError}
          />

          {error && (
            <div style={{ position: 'relative' }}>
              <FieldStatus
                style={{
                  marginTop: 0,
                  left: 0,
                }}
                text={`${i18next.t('Line')} ${error.line}, ${i18next.t('Column')} ${
                  error.column
                }: ${error.message}`}
              />
            </div>
          )}
        </div>

        <Panel height="48">
          <ButtonGroup stick="top" block>
            <Button stretch="block" size="large" onClick={handleSubmit}>
              <Text type="heading-6">{i18next.t('Submit')}</Text>
            </Button>
            <Button stretch="block" size="large" onMouseDown={handleCancel}>
              <Text type="heading-6">{i18next.t('Cancel')}</Text>
            </Button>
          </ButtonGroup>
        </Panel>
      </PanelGroup>

      <ViewportConsumer>
        {/* eslint-disable yola/react/no-danger */}
        {({ width }) => (
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .ws-html-widget-editor .ace_tooltip {
                  max-width: ${width}px;
                  transform: translate(${-modalOffsetX}px, ${-modalOffsetY}px);
                }
              `,
            }}
          />
        )}
        {/* eslint-enable yola/react/no-danger */}
      </ViewportConsumer>
    </Modal>
  );
};

EmbedHtmlSettingsDialog.propTypes = {
  elementId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default EmbedHtmlSettingsDialog;
